import { useCallback, useEffect, useState } from "react";
import { RiFileCopyLine, RiKey2Line, RiProhibited2Line } from "@remixicon/react";
import { toast } from "react-hot-toast";
import { Badge } from "../../components/Badge.tsx";
import { Button } from "../../components/Button.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { FieldGroup } from "../../components/Fieldset.tsx";
import Layout from "../../components/Layout.tsx";
import LoadingState from "../../components/LoadingState.tsx";
import { Select } from "../../components/Select.tsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/Table.tsx";
import { Text } from "../../components/Text.tsx";
import { useUser } from "../../context/UserContext.tsx";
import {
  createDataStudioApiKey,
  fetchDataStudioApiKeys,
  revokeDataStudioApiKey,
} from "../../services/ApiService.ts";
import { DataStudioApiKey } from "../../types.ts";
import { parseUtcDate } from "../../utils.ts";

const formatDate = (value: string) =>
  parseUtcDate(value).toLocaleDateString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const keyStatus = (key: DataStudioApiKey) => {
  if (key.revokedAt) {
    return { label: "Ingetrokken", color: "zinc" as const };
  }
  if (parseUtcDate(key.expiresAt) <= new Date()) {
    return { label: "Verlopen", color: "amber" as const };
  }
  return { label: "Actief", color: "green" as const };
};

const DataStudioApiKeys = () => {
  const { user } = useUser();
  const administrationCodes = user?.administrationCodes ?? [];
  const [administrationCode, setAdministrationCode] = useState<number | null>(
    administrationCodes.length === 1 ? administrationCodes[0] : null,
  );
  const [keys, setKeys] = useState<DataStudioApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeKey, setRevokeKey] = useState<DataStudioApiKey | null>(null);

  const loadKeys = useCallback(async () => {
    if (administrationCode === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await fetchDataStudioApiKeys(administrationCode);
    setLoading(false);
    if (error) {
      toast.error(error);
      return;
    }
    setKeys(data ?? []);
  }, [administrationCode]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async () => {
    if (administrationCode === null) return;
    const name = newKeyName.trim();
    if (!name) {
      toast.error("Geef de sleutel een naam");
      return;
    }

    setCreating(true);
    const { data, error } = await createDataStudioApiKey(administrationCode, name);
    setCreating(false);

    // The typed name stays in state so a failed attempt is corrected rather than retyped.
    if (error || !data) {
      toast.error(error ?? "Aanmaken mislukt");
      return;
    }

    setCreateOpen(false);
    setNewKeyName("");
    setIssuedToken(data.token);
    await loadKeys();
  };

  const handleRevoke = async () => {
    if (!revokeKey) return;
    setRevoking(true);
    const { error } = await revokeDataStudioApiKey(revokeKey.id);
    setRevoking(false);

    if (error) {
      toast.error(error);
      return;
    }

    setRevokeKey(null);
    toast.success("API-sleutel ingetrokken");
    await loadKeys();
  };

  const copyToken = async () => {
    if (!issuedToken) return;
    try {
      await navigator.clipboard.writeText(issuedToken);
      toast.success("Gekopieerd naar klembord");
    } catch {
      toast.error("Kopieren mislukt. Selecteer de sleutel en kopieer hem handmatig.");
    }
  };

  return (
    <Layout
      title="API-sleutels"
      description=""
      stepTitle="Data Studio API-sleutels"
      showBackButton={false}
      showNextButton={false}
    >
      <FieldGroup>
        <Text>
          Met een API-sleutel haal je je opgeslagen datasets op zonder het portaal te openen. Een
          sleutel geeft toegang tot jouw eigen datasets binnen een administratie en is een jaar
          geldig.
        </Text>

        {administrationCodes.length > 1 && (
          <div className="max-w-xs">
            <label htmlFor="administration" className="mb-1 block text-sm font-medium text-gray-700">
              Administratie
            </label>
            <Select
              id="administration"
              value={administrationCode ?? ""}
              onChange={(event) => setAdministrationCode(Number(event.target.value) || null)}
            >
              <option value="">Kies een administratie</option>
              {administrationCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </div>
        )}

        {administrationCode !== null && (
          <div className="flex justify-end">
            <Button color="primary" onClick={() => setCreateOpen(true)}>
              Nieuwe sleutel
            </Button>
          </div>
        )}

        {loading ? (
          <LoadingState />
        ) : administrationCode === null ? (
          <Text>Kies eerst een administratie om je API-sleutels te zien.</Text>
        ) : (
          <Table dense>
            <TableHead>
              <TableRow>
                <TableHeader>Naam</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Aangemaakt</TableHeader>
                <TableHeader>Verloopt</TableHeader>
                <TableHeader>Acties</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => {
                const status = keyStatus(key);
                return (
                  <TableRow key={key.id}>
                    <TableCell>{key.name}</TableCell>
                    <TableCell>
                      <Badge color={status.color}>{status.label}</Badge>
                    </TableCell>
                    <TableCell>{formatDate(key.createdAt)}</TableCell>
                    <TableCell>{formatDate(key.expiresAt)}</TableCell>
                    <TableCell>
                      {key.revokedAt ? null : (
                        <button
                          type="button"
                          onClick={() => setRevokeKey(key)}
                          className="rounded-lg p-1.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                          title="Sleutel intrekken"
                          aria-label={"Sleutel " + key.name + " intrekken"}
                        >
                          <RiProhibited2Line className="h-5 w-5" />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {keys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500">
                    Je hebt nog geen API-sleutels. Maak er een aan om je datasets op te halen met
                    een HTTP-verzoek.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </FieldGroup>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)}>
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900">Nieuwe API-sleutel</h3>
          <p className="mt-2 text-sm text-gray-500">
            Geef de sleutel een naam die je later herkent, bijvoorbeeld het script of rapport dat
            hem gebruikt.
          </p>
          <label htmlFor="key-name" className="mt-4 block text-sm font-medium text-gray-700">
            Naam
          </label>
          <input
            id="key-name"
            type="text"
            value={newKeyName}
            onChange={(event) => setNewKeyName(event.target.value)}
            placeholder="Bijvoorbeeld: maandrapportage"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            autoFocus
            onKeyDown={(event) => {
              if (event.key === "Enter") handleCreate();
              if (event.key === "Escape" && !creating) setCreateOpen(false);
            }}
          />
          <div className="mt-6 flex w-full gap-3">
            <Button
              color="light"
              className="flex-1"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
            >
              Annuleren
            </Button>
            <Button color="primary" className="flex-1" onClick={handleCreate} disabled={creating}>
              {creating ? "Aanmaken..." : "Aanmaken"}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={issuedToken !== null} onClose={() => setIssuedToken(null)}>
        <div className="flex flex-col">
          <div className="flex h-12 w-12 items-center justify-center self-center rounded-full bg-green-100">
            <RiKey2Line className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="mt-4 text-center text-lg font-semibold text-gray-900">
            Sleutel aangemaakt
          </h3>
          <p className="mt-2 text-center text-sm text-gray-500">
            Kopieer de sleutel nu. Zodra je dit venster sluit is hij niet meer op te vragen en moet
            je een nieuwe aanmaken.
          </p>
          <code className="mt-4 block break-all rounded-lg bg-gray-50 p-3 font-mono text-xs text-gray-800">
            {issuedToken}
          </code>
          <p className="mt-3 text-sm text-gray-500">
            Stuur hem mee als <span className="font-medium text-gray-700">X-Api-Key</span>-header
            naar <span className="font-medium text-gray-700">/api/datastudio/v1/datasets</span>.
          </p>
          <div className="mt-6 flex w-full gap-3">
            <Button color="light" className="flex-1" onClick={copyToken}>
              <RiFileCopyLine className="h-5 w-5" />
              Kopieren
            </Button>
            <Button color="primary" className="flex-1" onClick={() => setIssuedToken(null)}>
              Sluiten
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={revokeKey !== null} onClose={() => !revoking && setRevokeKey(null)}>
        <div className="flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <RiProhibited2Line className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">API-sleutel intrekken</h3>
          <p className="mt-2 text-sm text-gray-500">
            Weet je zeker dat je <span className="font-medium text-gray-700">{revokeKey?.name}</span>{" "}
            wilt intrekken? Scripts die deze sleutel gebruiken werken daarna niet meer.
          </p>
          <div className="mt-6 flex w-full gap-3">
            <Button
              color="light"
              className="flex-1"
              onClick={() => setRevokeKey(null)}
              disabled={revoking}
            >
              Annuleren
            </Button>
            <Button color="red" className="flex-1" onClick={handleRevoke} disabled={revoking}>
              {revoking ? "Intrekken..." : "Intrekken"}
            </Button>
          </div>
        </div>
      </Dialog>
    </Layout>
  );
};

export default DataStudioApiKeys;
